import {messages} from '@smartmixin/i18n';
import {CheckCircle, ThumbsDown, ThumbsUp} from '@phosphor-icons/react';
import {Box, Typography} from '@mui/material';
import {useEffect, useId, useRef, useState} from 'react';
import ReactGA from 'react-ga4';
import {useIntl} from 'react-intl';
import {Button, Surface} from '../../components/ui';

type ArticleFeedbackProps = {
  articleId: string;
};

// GA reason values (kebab-case per analytics conventions)
const REASONS = [
  {value: 'outdated', message: messages.helpCenter.feedbackReasonOutdated},
  {value: 'unclear', message: messages.helpCenter.feedbackReasonUnclear},
  {value: 'not-answer', message: messages.helpCenter.feedbackReasonNotAnswer},
];

type Step = 'ask' | 'reason' | 'thanks';

/**
 * "Was this article helpful?" Yes / No; a No asks why (one tap, skippable).
 * The buttons that were pressed disappear, so each step takes the focus
 * (it fell to <body> before) and the thanks is a polite status.
 */
const ArticleFeedback = ({articleId}: ArticleFeedbackProps) => {
  const {formatMessage} = useIntl();
  const [step, setStep] = useState<Step>('ask');
  const stepRef = useRef<HTMLDivElement>(null);
  const questionId = useId();
  const moved = useRef(false);

  useEffect(() => {
    if (moved.current) {
      stepRef.current?.focus();
    }
  }, [step]);

  const go = (next: Step) => {
    moved.current = true;
    setStep(next);
  };

  const handleFeedback = (value: 'helpful' | 'not-helpful') => {
    ReactGA.gtag('event', 'article_feedback', {
      article_id: articleId,
      feedback: value,
    });
    go(value === 'helpful' ? 'thanks' : 'reason');
  };

  const handleReason = (reason: string) => {
    ReactGA.gtag('event', 'article_feedback_reason', {
      article_id: articleId,
      reason,
    });
    go('thanks');
  };

  const frame = {
    p: 2.5,
    mt: 4,
    textAlign: 'center',
    // The step takes focus programmatically; the ring sits inside the card.
    '&&:focus-visible': {outlineOffset: '-2px'},
    '&:focus': {outline: 'none'},
  } as const;
  const row = {
    display: 'flex',
    gap: 1,
    justifyContent: 'center',
    flexWrap: 'wrap',
  } as const;

  if (step === 'reason') {
    return (
      <Surface
        ref={stepRef}
        tabIndex={-1}
        role="group"
        aria-labelledby={questionId}
        sx={frame}>
        <Typography
          id={questionId}
          variant="title"
          component="p"
          sx={{color: 'text.primary', mb: 1.5}}>
          {formatMessage(messages.helpCenter.feedbackWhatWentWrong)}
        </Typography>
        <Box sx={{...row, mb: 1}}>
          {REASONS.map((reason) => (
            <Button
              key={reason.value}
              variant="secondary"
              size="small"
              onClick={() => handleReason(reason.value)}>
              {formatMessage(reason.message)}
            </Button>
          ))}
        </Box>
        <Button variant="tertiary" size="small" onClick={() => go('thanks')}>
          {formatMessage(messages.common.skip)}
        </Button>
      </Surface>
    );
  }

  if (step === 'thanks') {
    return (
      <Surface ref={stepRef} tabIndex={-1} role="status" sx={frame}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'text.primary',
          }}>
          <Box
            component="span"
            aria-hidden
            sx={{display: 'flex', color: 'success.main'}}>
            <CheckCircle size={18} weight="fill" />
          </Box>
          <Typography variant="title" component="span">
            {formatMessage(messages.helpCenter.thankYouForFeedback)}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{color: 'text.secondary', mt: 0.5}}>
          {formatMessage(messages.helpCenter.feedbackHelpsImprove)}
        </Typography>
      </Surface>
    );
  }

  return (
    <Surface role="group" aria-labelledby={questionId} sx={frame}>
      <Typography
        id={questionId}
        variant="title"
        component="p"
        sx={{color: 'text.primary', mb: 1.5}}>
        {formatMessage(messages.helpCenter.wasArticleHelpful)}
      </Typography>
      <Box sx={row}>
        <Button
          variant="secondary"
          startIcon={<ThumbsUp aria-hidden />}
          onClick={() => handleFeedback('helpful')}>
          {formatMessage(messages.common.yes)}
        </Button>
        <Button
          variant="secondary"
          startIcon={<ThumbsDown aria-hidden />}
          onClick={() => handleFeedback('not-helpful')}>
          {formatMessage(messages.common.no)}
        </Button>
      </Box>
    </Surface>
  );
};

export default ArticleFeedback;
